'use client';

import { useState } from 'react';
import Image from 'next/image';
import Link from 'next/link';
import { useParams } from 'next/navigation';
import { Section } from '@/components/ui/Section';
import { Container } from '@/components/ui/Container';
import { Heading } from '@/components/ui/Heading';
import { Text } from '@/components/ui/Text';
import { Button } from '@/components/ui/Button';
import { getLocalePath, type Locale } from '@/lib/i18n';
import styles from './BranchSolutionsShowcase.module.css';

interface Feature {
  [key: string]: string;
}

interface Solution {
  title: string;
  description: string;
  features: Feature | string[];
  cta: string;
  ctaLink: string;
  image: string;
  imageAlt: string;
  isMobileImage?: boolean; // true for mobile screenshots, false for landscape photos
}

interface BranchSolutionsShowcaseProps {
  title: string;
  subtitle: string;
  solutions: Solution[];
  background?: 'primary' | 'secondary';
}

export default function BranchSolutionsShowcase({
  title,
  subtitle,
  solutions,
  background = 'primary',
}: BranchSolutionsShowcaseProps) {
  const params = useParams();
  const locale = (params?.locale as Locale) || 'en';
  const [expandedSolutions, setExpandedSolutions] = useState<Set<number>>(new Set());

  const toggleExpanded = (index: number) => {
    const newExpanded = new Set(expandedSolutions);
    if (newExpanded.has(index)) {
      newExpanded.delete(index);
    } else {
      newExpanded.add(index);
    }
    setExpandedSolutions(newExpanded);
  };

  const showMoreText = locale === 'nl' ? 'Toon meer' : locale === 'de' ? 'Mehr anzeigen' : 'Show more';
  const showLessText = locale === 'nl' ? 'Toon minder' : locale === 'de' ? 'Weniger anzeigen' : 'Show less';

  // Function to get appropriate icon based on feature text
  const getFeatureIcon = (feature: string) => {
    const lowerFeature = feature.toLowerCase();
    
    // Employee badge, link, koppeling, medewerker
    if (lowerFeature.includes('employee badge') || lowerFeature.includes('medewerker') || lowerFeature.includes('badge') || lowerFeature.includes('koppeling')) {
      return (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <rect x="4" y="5" width="16" height="16" rx="2" stroke="currentColor" strokeWidth="2"/>
          <circle cx="12" cy="10" r="3" stroke="currentColor" strokeWidth="2"/>
          <path d="M8 19c0-2 1.5-3 4-3s4 1 4 3" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
        </svg>
      );
    }
    
    // Card payment, direct payment, invoicing, factuur
    if (lowerFeature.includes('card payment') || lowerFeature.includes('direct payment') || lowerFeature.includes('invoicing') || lowerFeature.includes('pin') || lowerFeature.includes('betaalpas')) {
      return (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <rect x="2" y="6" width="20" height="12" rx="2" stroke="currentColor" strokeWidth="2"/>
          <path d="M2 10h20" stroke="currentColor" strokeWidth="2"/>
          <circle cx="7" cy="14" r="1" fill="currentColor"/>
        </svg>
      );
    }
    
    // Time-based, tijd, daytime, time of day
    if (lowerFeature.includes('time-based') || lowerFeature.includes('time of day') || lowerFeature.includes('daytime') || lowerFeature.includes('dagdeel') || lowerFeature.includes('tijdstip')) {
      return (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="2"/>
          <path d="M12 7v5l3 3" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
          <path d="M12 3v1M21 12h-1M12 21v-1M3 12h1" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
        </svg>
      );
    }
    
    // Combo, deals, lunch menu, menu's
    if (lowerFeature.includes('combo') || lowerFeature.includes('deal') || lowerFeature.includes('lunch menu') || lowerFeature.includes('meal deal')) {
      return (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <circle cx="9" cy="7" r="4" stroke="currentColor" strokeWidth="2"/>
          <circle cx="16" cy="16" r="4" stroke="currentColor" strokeWidth="2"/>
          <path d="M13 7h3M9 11v3M12 16h4" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
        </svg>
      );
    }
    
    // Split bill, splitsen, apart
    if (lowerFeature.includes('split') || lowerFeature.includes('splitsen') || lowerFeature.includes('apart betalen')) {
      return (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <rect x="3" y="4" width="7" height="16" rx="1" stroke="currentColor" strokeWidth="2"/>
          <rect x="14" y="4" width="7" height="16" rx="1" stroke="currentColor" strokeWidth="2"/>
          <path d="M6 9h2M6 13h2M17 9h2M17 13h2" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
        </svg>
      );
    }
    
    // Diet options, dietary, dieet, voeding
    if (lowerFeature.includes('diet') || lowerFeature.includes('dietary') || lowerFeature.includes('dieet') || lowerFeature.includes('voeding')) {
      return (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="2"/>
          <path d="M8 12h8M12 8v8" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
          <path d="M15 9l-6 6M9 9l6 6" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" opacity="0.5"/>
        </svg>
      );
    }
    
    // Order taking, nemen, opnemen
    if (lowerFeature.includes('order taking') || lowerFeature.includes('opnemen') || lowerFeature.includes('bestelling opnemen')) {
      return (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <rect x="5" y="2" width="14" height="20" rx="2" stroke="currentColor" strokeWidth="2"/>
          <path d="M9 7h6M9 11h6M9 15h4" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
          <circle cx="16" cy="18" r="1" fill="currentColor"/>
        </svg>
      );
    }
    
    // Department, guest, link to
    if (lowerFeature.includes('department') || lowerFeature.includes('guest') || lowerFeature.includes('link to') || lowerFeature.includes('afdeling')) {
      return (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M10 13a5 5 0 007.54.54l3-3a5 5 0 00-7.07-7.07l-1.72 1.71" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          <path d="M14 11a5 5 0 00-7.54-.54l-3 3a5 5 0 007.07 7.07l1.71-1.71" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
      );
    }
    
    // Throughput, doorloop, lunch break, rush
    if (lowerFeature.includes('throughput') || lowerFeature.includes('doorloop') || lowerFeature.includes('lunch break') || lowerFeature.includes('lunchpauze') || lowerFeature.includes('rush')) {
      return (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M5 12h14M16 8l4 4-4 4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          <circle cx="5" cy="12" r="2" stroke="currentColor" strokeWidth="2"/>
        </svg>
      );
    }
    
    // Fast, speed, quick, rapid, snelle
    if (lowerFeature.includes('fast') || lowerFeature.includes('quick') || lowerFeature.includes('rapid') || lowerFeature.includes('snelle') || lowerFeature.includes('snel')) {
      return (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M13 2L3 14h8l-1 8 10-12h-8l1-8z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
      );
    }
    
    // Premium, VIP, luxury, exclusief
    if (lowerFeature.includes('premium') || lowerFeature.includes('vip') || lowerFeature.includes('luxury') || lowerFeature.includes('exclusief') || lowerFeature.includes('exclusive')) {
      return (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M12 2L15.09 8.26L22 9.27L17 14.14L18.18 21.02L12 17.77L5.82 21.02L7 14.14L2 9.27L8.91 8.26L12 2Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
      );
    }
    
    // Pay, payment, betalen, betaling, invoice, factu
    if (lowerFeature.includes('pay') || lowerFeature.includes('payment') || lowerFeature.includes('betaal') || lowerFeature.includes('betaling') || lowerFeature.includes('invoice') || lowerFeature.includes('factuur')) {
      return (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <rect x="2" y="5" width="20" height="14" rx="2" stroke="currentColor" strokeWidth="2"/>
          <path d="M2 10h20" stroke="currentColor" strokeWidth="2"/>
        </svg>
      );
    }
    
    // Offline, download, sync
    if (lowerFeature.includes('offline') || lowerFeature.includes('download') || lowerFeature.includes('sync')) {
      return (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4M7 10l5 5 5-5M12 15V3" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
      );
    }
    
    // Customizable, aanpasbaar, configure, settings, instellen
    if (lowerFeature.includes('customiz') || lowerFeature.includes('aanpasbaar') || lowerFeature.includes('configure') || lowerFeature.includes('instelbaar') || lowerFeature.includes('personalis')) {
      return (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <circle cx="12" cy="12" r="3" stroke="currentColor" strokeWidth="2"/>
          <path d="M12 1v6m0 6v6M23 12h-6m-6 0H1" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
        </svg>
      );
    }
    
    // Pre-order, voorbestellen, advance
    if (lowerFeature.includes('pre-order') || lowerFeature.includes('voorbestel') || lowerFeature.includes('advance order') || lowerFeature.includes('van tevoren')) {
      return (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <rect x="3" y="6" width="18" height="15" rx="2" stroke="currentColor" strokeWidth="2"/>
          <path d="M16 2v4M8 2v4M3 10h18" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
          <path d="M8 14h3m-3 4h5" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
        </svg>
      );
    }
    
    // Automatic, timing, automation, automatisch
    if (lowerFeature.includes('automatic') || lowerFeature.includes('timing') || lowerFeature.includes('automation') || lowerFeature.includes('automatisch')) {
      return (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2"/>
          <path d="M12 6v6l4 2" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
        </svg>
      );
    }
    
    // Routing, routing, navigation
    if (lowerFeature.includes('routing') || lowerFeature.includes('navigation') || lowerFeature.includes('route')) {
      return (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <circle cx="18" cy="18" r="3" stroke="currentColor" strokeWidth="2"/>
          <circle cx="6" cy="6" r="3" stroke="currentColor" strokeWidth="2"/>
          <path d="M13 6h3a2 2 0 012 2v7" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
        </svg>
      );
    }
    
    // Age, alcohol, restriction, verification, leeftijd
    if (lowerFeature.includes('age') || lowerFeature.includes('alcohol') || lowerFeature.includes('restriction') || lowerFeature.includes('verification') || lowerFeature.includes('leeftijd') || lowerFeature.includes('verificatie')) {
      return (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <rect x="4" y="4" width="16" height="16" rx="2" stroke="currentColor" strokeWidth="2"/>
          <path d="M8 8h8M8 12h5M8 16h6" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
          <circle cx="16" cy="16" r="2" fill="currentColor"/>
        </svg>
      );
    }
    
    // Pickup code, ticket, code, afhaalcode
    if (lowerFeature.includes('pickup code') || lowerFeature.includes('ticket') || lowerFeature.includes('code on screen') || lowerFeature.includes('afhaalcode')) {
      return (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <rect x="4" y="4" width="16" height="16" rx="2" stroke="currentColor" strokeWidth="2"/>
          <path d="M9 9h6M9 13h6M9 17h4" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
        </svg>
      );
    }
    
    // 24/7, self-service, without staff
    if (lowerFeature.includes('24/7') || lowerFeature.includes('self-service') || lowerFeature.includes('without staff') || lowerFeature.includes('zonder personeel') || lowerFeature.includes('zelfbediening')) {
      return (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="2"/>
          <path d="M12 6V12L16 14" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
        </svg>
      );
    }
    
    // QR, barcode, scanner
    if (lowerFeature.includes('qr') || lowerFeature.includes('barcode') || lowerFeature.includes('scanner') || lowerFeature.includes('scan')) {
      return (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <rect x="3" y="3" width="7" height="7" stroke="currentColor" strokeWidth="2"/>
          <rect x="14" y="3" width="7" height="7" stroke="currentColor" strokeWidth="2"/>
          <rect x="3" y="14" width="7" height="7" stroke="currentColor" strokeWidth="2"/>
          <path d="M14 14H21M14 17H21M14 20H21" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
        </svg>
      );
    }
    
    // Languages, multilingual
    if (lowerFeature.includes('language') || lowerFeature.includes('multilingual') || lowerFeature.includes('talen') || lowerFeature.includes('sprachen') || lowerFeature.includes('meertalig')) {
      return (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="2"/>
          <path d="M3 12H21M12 3C14.5 6 15 9 15 12C15 15 14.5 18 12 21M12 3C9.5 6 9 9 9 12C9 15 9.5 18 12 21" stroke="currentColor" strokeWidth="2"/>
        </svg>
      );
    }
    
    // Groups, groepen, companies
    if (lowerFeature.includes('group') || lowerFeature.includes('groep') || lowerFeature.includes('companies') || lowerFeature.includes('bedrijven') || lowerFeature.includes('organizer')) {
      return (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          <circle cx="9" cy="7" r="4" stroke="currentColor" strokeWidth="2"/>
          <path d="M23 21v-2a4 4 0 00-3-3.87M16 3.13a4 4 0 010 7.75" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
      );
    }
    
    // Visual, visueel, cocktails, drinks
    if (lowerFeature.includes('visual') || lowerFeature.includes('visueel') || lowerFeature.includes('cocktail') || lowerFeature.includes('drink') || lowerFeature.includes('snack')) {
      return (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M12 22v-6M8 8h8l-1 6H9L8 8z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          <path d="M8.5 8a4.5 4.5 0 017 0" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
        </svg>
      );
    }
    
    // Discrete, discreet, quiet, stil
    if (lowerFeature.includes('discrete') || lowerFeature.includes('discreet') || lowerFeature.includes('quiet') || lowerFeature.includes('stil') || lowerFeature.includes('without disturbing')) {
      return (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M11 5L6 9H2v6h4l5 4V5z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          <path d="M23 9l-6 6M17 9l6 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
      );
    }
    
    // Account, rekening, add to bill
    if (lowerFeature.includes('account') || lowerFeature.includes('rekening') || lowerFeature.includes('add to') || lowerFeature.includes('toevoegen aan')) {
      return (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8l-6-6z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          <path d="M14 2v6h6M12 18v-6M9 15h6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
      );
    }
    
    // Recording, recording, opname
    if (lowerFeature.includes('recording') || lowerFeature.includes('opname') || lowerFeature.includes('record')) {
      return (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2"/>
          <circle cx="12" cy="12" r="3" fill="currentColor"/>
        </svg>
      );
    }
    
    // Location, lobby, minimarket, wellness, pool
    if (lowerFeature.includes('lobby') || lowerFeature.includes('minimarket') || lowerFeature.includes('wellness') || lowerFeature.includes('pool') || lowerFeature.includes('bowling') || lowerFeature.includes('entertainment')) {
      return (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0118 0z" stroke="currentColor" strokeWidth="2"/>
          <circle cx="12" cy="10" r="3" stroke="currentColor" strokeWidth="2"/>
        </svg>
      );
    }
    
    // Checkout, payment, self-checkout, queues
    if (lowerFeature.includes('checkout') || lowerFeature.includes('queue') || lowerFeature.includes('payment') || lowerFeature.includes('afrekenen')) {
      return (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <rect x="3" y="6" width="18" height="12" rx="2" stroke="currentColor" strokeWidth="2"/>
          <path d="M7 10H17M7 14H13" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
        </svg>
      );
    }
    
    // Room service, delivery, orders
    if (lowerFeature.includes('room service') || lowerFeature.includes('delivery') || lowerFeature.includes('phone call') || lowerFeature.includes('bezorging') || lowerFeature.includes('kamerdienst')) {
      return (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M3 11L12 3L21 11V19C21 19.5304 20.7893 20.0391 20.4142 20.4142C20.0391 20.7893 19.5304 21 19 21H5C4.46957 21 3.96086 20.7893 3.58579 20.4142C3.21071 20.0391 3 19.5304 3 19V11Z" stroke="currentColor" strokeWidth="2"/>
          <path d="M9 21V12H15V21" stroke="currentColor" strokeWidth="2"/>
        </svg>
      );
    }
    
    // Menu, digital, tablet, photos
    if (lowerFeature.includes('menu') || lowerFeature.includes('digital') || lowerFeature.includes('tablet') || lowerFeature.includes('photo') || lowerFeature.includes("foto's")) {
      return (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <rect x="4" y="4" width="16" height="16" rx="2" stroke="currentColor" strokeWidth="2"/>
          <path d="M8 8H16M8 12H16M8 16H12" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
        </svg>
      );
    }
    
    // Reservation, booking, time slots
    if (lowerFeature.includes('reservation') || lowerFeature.includes('booking') || lowerFeature.includes('time slot') || lowerFeature.includes('reservering') || lowerFeature.includes('tijdslots')) {
      return (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <rect x="3" y="6" width="18" height="15" rx="2" stroke="currentColor" strokeWidth="2"/>
          <path d="M8 4V8M16 4V8M3 10H21" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
        </svg>
      );
    }
    
    // Integration, POS, website
    if (lowerFeature.includes('integration') || lowerFeature.includes('pos') || lowerFeature.includes('website') || lowerFeature.includes('integratie')) {
      return (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M12 2L2 7L12 12L22 7L12 2Z" stroke="currentColor" strokeWidth="2" strokeLinejoin="round"/>
          <path d="M2 17L12 22L22 17M2 12L12 17L22 12" stroke="currentColor" strokeWidth="2" strokeLinejoin="round"/>
        </svg>
      );
    }
    
    // Service requests, maintenance, towels
    if (lowerFeature.includes('service request') || lowerFeature.includes('maintenance') || lowerFeature.includes('towel') || lowerFeature.includes('handdoeken') || lowerFeature.includes('onderhoud')) {
      return (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M12 2L2 7L12 12L22 7L12 2Z" stroke="currentColor" strokeWidth="2" strokeLinejoin="round"/>
          <path d="M2 17L12 22L22 17" stroke="currentColor" strokeWidth="2" strokeLinejoin="round"/>
          <path d="M12 12V22" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
        </svg>
      );
    }
    
    // Opening hours, category, kitchen, bar
    if (lowerFeature.includes('opening hour') || lowerFeature.includes('category') || lowerFeature.includes('kitchen') || lowerFeature.includes('openingstijden') || lowerFeature.includes('categorie')) {
      return (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="2"/>
          <path d="M12 6V12L16 14" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
        </svg>
      );
    }
    
    // Allergen, pregnancy, filter
    if (lowerFeature.includes('allergen') || lowerFeature.includes('pregnancy') || lowerFeature.includes('filter') || lowerFeature.includes('allergenen') || lowerFeature.includes('zwangerschap')) {
      return (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M12 2L2 7L12 12L22 7L12 2Z" stroke="currentColor" strokeWidth="2" strokeLinejoin="round"/>
          <path d="M2 12L12 17L22 12" stroke="currentColor" strokeWidth="2" strokeLinejoin="round"/>
          <circle cx="12" cy="18" r="3" stroke="currentColor" strokeWidth="2"/>
        </svg>
      );
    }
    
    // Delivery fee, calculation, minimum order
    if (lowerFeature.includes('delivery fee') || lowerFeature.includes('calculation') || lowerFeature.includes('minimum order') || lowerFeature.includes('bezorgkosten') || lowerFeature.includes('minimumbedrag')) {
      return (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <rect x="3" y="6" width="18" height="11" rx="2" stroke="currentColor" strokeWidth="2"/>
          <path d="M7 17V20M17 17V20M7 6V3M17 6V3" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
        </svg>
      );
    }
    
    // Course, dinner, all-you-can-eat
    if (lowerFeature.includes('course') || lowerFeature.includes('dinner') || lowerFeature.includes('all-you-can-eat') || lowerFeature.includes('gang') || lowerFeature.includes('diner')) {
      return (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="2"/>
          <path d="M8 12L11 15L16 9" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
      );
    }
    
    // External visitors, delivery, pickup
    if (lowerFeature.includes('external') || lowerFeature.includes('visitor') || lowerFeature.includes('pickup') || lowerFeature.includes('bezoekers') || lowerFeature.includes('ophalen')) {
      return (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M20 21V19C20 17.9391 19.5786 16.9217 18.8284 16.1716C18.0783 15.4214 17.0609 15 16 15H8C6.93913 15 5.92172 15.4214 5.17157 16.1716C4.42143 16.9217 4 17.9391 4 19V21" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          <circle cx="12" cy="7" r="4" stroke="currentColor" strokeWidth="2"/>
        </svg>
      );
    }
    
    // Branded, webshop
    if (lowerFeature.includes('branded') || lowerFeature.includes('webshop') || lowerFeature.includes('gebrande')) {
      return (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M4 19V5C4 3.89543 4.89543 3 6 3H18C19.1046 3 20 3.89543 20 5V19C20 20.1046 19.1046 21 18 21H6C4.89543 21 4 20.1046 4 19Z" stroke="currentColor" strokeWidth="2"/>
          <path d="M8 8H16M8 12H16M8 16H12" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
        </svg>
      );
    }
    
    // Default checkmark
    return (
      <svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M11.5 3.5L5.25 9.75L2.5 7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
      </svg>
    );
  };

  return (
    <Section background={background}>
      <Container>
        {/* SVG Gradient Definition */}
        <svg width="0" height="0" style={{ position: 'absolute' }}>
          <defs>
            <linearGradient id="iconGradient" x1="0%" y1="0%" x2="100%" y2="100%">
              <stop offset="0%" style={{ stopColor: '#FF5E62', stopOpacity: 1 }} />
              <stop offset="100%" style={{ stopColor: '#FF9966', stopOpacity: 1 }} />
            </linearGradient>
          </defs>
        </svg>

        <div className={styles.header}>
          <Heading as="h2" className={styles.title}>
            {title}
          </Heading>
          <Text size="lg" color="secondary" className={styles.subtitle}>
            {subtitle}
          </Text>
        </div>

        <div className={styles.solutionsGrid}>
          {solutions.map((solution, index) => {
            const allFeatures = Array.isArray(solution.features) 
              ? solution.features 
              : Object.values(solution.features);
            const isExpanded = expandedSolutions.has(index);
            const visibleFeatures = isExpanded ? allFeatures : allFeatures.slice(0, 4);
            const hasMoreFeatures = allFeatures.length > 4;

            return (
              <div 
                key={index} 
                className={`${styles.solutionCard} ${index % 2 === 1 ? styles.reverse : ''}`}
              >
                <div className={`${styles.solutionImage} ${solution.isMobileImage ? styles.mobileImage : ''}`}>
                  <Image
                    src={solution.image}
                    alt={solution.imageAlt}
                    width={600}
                    height={400}
                    className={styles.image}
                    loading="eager"
                    priority={true}
                    quality={85}
                    sizes="(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 600px"
                  />
                </div>
                
                <div className={styles.solutionContent} data-number={index + 1}>
                  <Heading as="h3" className={styles.solutionTitle}>
                    {solution.title}
                  </Heading>
                  <Text size="md" color="secondary" className={styles.solutionDescription}>
                    {solution.description}
                  </Text>
                  
                  <ul className={styles.featuresList}>
                    {visibleFeatures.map((feature, idx) => (
                      <li 
                        key={idx} 
                        className={`${styles.featureItem} ${idx >= 4 && isExpanded ? styles.fadeIn : ''}`}
                      >
                        <div className={styles.featureIcon}>
                          {getFeatureIcon(feature)}
                        </div>
                        {feature}
                      </li>
                    ))}
                  </ul>

                  {hasMoreFeatures && (
                    <button
                      onClick={() => toggleExpanded(index)}
                      className={styles.showMoreButton}
                      aria-expanded={isExpanded}
                    >
                      <span>{isExpanded ? showLessText : showMoreText}</span>
                      <svg
                        width="16"
                        height="16"
                        viewBox="0 0 16 16"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                        className={styles.chevronIcon}
                        style={{ transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)' }}
                      >
                        <path
                          d="M4 6L8 10L12 6"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    </button>
                  )}
                  
                  <Link href={getLocalePath(locale, solution.ctaLink)}>
                    <Button variant="secondary" size="md" className={styles.ctaButton}>
                      {solution.cta}
                    </Button>
                  </Link>
                </div>
              </div>
            );
          })}
        </div>
      </Container>
    </Section>
  );
}

